import { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Group,
  Badge,
  Breadcrumbs,
  Anchor,
  Grid,
  Select,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import { Link, useLocation } from "wouter";
import { useBrandAwarenessStore } from "./brandAwarenessStore";
import { useBrandsStore } from "../brandsStore";
import { useBrandContext } from "../brandContext";

export default function CriterionDetail({ params }) {
  const [location] = useLocation();
  const { 
    industryDetails, 
    isLoading, 
    getIndustryDetailsByBrandId 
  } = useBrandAwarenessStore();
  
  const { getBrandById, brands } = useBrandsStore();
  const { selectBrandById } = useBrandContext();
  const [selectedModel, setSelectedModel] = useState("all");

  useEffect(() => {
    getBrandById(params.brandId);
    selectBrandById(params.brandId);
  }, [params.brandId, getBrandById, selectBrandById]);

  useEffect(() => {
    if (params.industry) {
      getIndustryDetailsByBrandId(params.brandId, decodeURIComponent(params.industry));
    }
  }, [params.brandId, params.industry, getIndustryDetailsByBrandId]);

  const brand = brands?.find((b) => b.id === params.brandId);
  const industry = decodeURIComponent(params.industry);
  const criterion = decodeURIComponent(params.criterion);
  const details = industryDetails[`${params.brandId}_${industry}`];

  // Filter details for this specific criterion
  const criterionDetails = details?.filter(item => item.criterion === criterion) || [];
  
  // Get all unique models
  const availableModels = [...new Set(criterionDetails.map(item => item.llmModelName))];

  // Transform data for charts by model
  const transformDataByModel = () => {
    if (!criterionDetails || criterionDetails.length === 0) return {};

    if (selectedModel === "all") {
      // Group by date and aggregate across models
      const dateGroups = criterionDetails.reduce((acc, item) => {
        const date = new Date(item.date).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);
        return acc;
      }, {});

      const aggregatedData = Object.entries(dateGroups)
        .map(([date, items]) => {
          const dataPoint = { date };
          
          // Average position across models
          const avgPosition = items.length > 0 
            ? items.reduce((sum, item) => sum + (item.averagePosition || 0), 0) / items.length
            : 0;
          
          if (avgPosition > 0) {
            dataPoint[brand?.name || "Monitored Brand"] = avgPosition;
          }

          // Get top brands (using the most recent model's data for simplicity)
          const latestItem = items[0];
          latestItem.topBrands.slice(0, 5).forEach((brandName, index) => {
            if (brandName !== (brand?.name || "Monitored Brand")) {
              dataPoint[brandName] = index + 1;
            }
          });

          return dataPoint;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      return { "All Models (Averaged)": aggregatedData };
    } else {
      // Show data for specific model
      const modelData = criterionDetails
        .filter(item => item.llmModelName === selectedModel)
        .map(item => {
          const dataPoint = {
            date: new Date(item.date).toLocaleDateString(),
          };

          if (item.averagePosition > 0) {
            dataPoint[brand?.name || "Monitored Brand"] = item.averagePosition;
          }

          item.topBrands.slice(0, 5).forEach((brandName, index) => {
            if (brandName !== (brand?.name || "Monitored Brand")) {
              dataPoint[brandName] = index + 1;
            }
          });

          return dataPoint;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      return { [selectedModel]: modelData };
    }
  };

  const chartData = transformDataByModel();

  const breadcrumbItems = [
    { title: brand?.name || "Brand", href: "/dashboard" },
    { title: "AI Visibility", href: "/ai-visibility" },
    { title: industry, href: "/ai-visibility" },
    { title: criterion, href: location },
  ].map((item, index) => (
    <Anchor component={Link} href={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  if (isLoading) {
    return (
      <Container>
        <Text>Loading criterion details...</Text>
      </Container>
    );
  }

  if (!criterionDetails || criterionDetails.length === 0) {
    return (
      <Container>
        <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        <Title order={1} mt="lg" mb="md">
          {criterion} - {industry}
        </Title>
        <Text>No data available for this criterion.</Text>
      </Container>
    );
  }

  const monitoredBrandName = brand?.name || "Monitored Brand";

  return (
    <Container size="xl">
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      
      <Title order={1} mb="md">
        {criterion}
      </Title>
      
      <Text size="lg" c="dimmed" mb="xl">
        Detailed position tracking for <strong>{monitoredBrandName}</strong> in{" "}
        <strong>{industry}</strong> across different LLM models
      </Text>

      <Grid mb="xl">
        <Grid.Col span={6}>
          <Select
            label="Model View"
            value={selectedModel}
            onChange={setSelectedModel}
            data={[
              { value: "all", label: "All Models (Averaged)" },
              ...availableModels.map(model => ({ value: model, label: model }))
            ]}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">Summary</Text>
            <Group gap="md">
              <div>
                <Text size="xs" c="dimmed">Total Measurements</Text>
                <Badge variant="light" color="blue">
                  {criterionDetails.length}
                </Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Models</Text>
                <Badge variant="light" color="green">
                  {availableModels.length}
                </Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">Latest Mentions</Text>
                <Badge variant="light" color="orange">
                  {criterionDetails[criterionDetails.length - 1]?.mentionCount || 0}
                </Badge>
              </div>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      <Stack gap="xl">
        {Object.entries(chartData).map(([modelName, data]) => {
          // Get all brand names from the data (excluding date)
          const allBrands = new Set();
          data.forEach((dataPoint) => {
            Object.keys(dataPoint).forEach((key) => {
              if (key !== "date") {
                allBrands.add(key);
              }
            });
          });

          // Create series configuration - highlight the monitored brand
          const series = Array.from(allBrands).map((brandName) => ({
            name: brandName,
            color: brandName === monitoredBrandName ? "#228be6" : "#ced4da",
            strokeWidth: brandName === monitoredBrandName ? 3 : 1,
          }));

          return (
            <Paper key={modelName} p="lg" withBorder shadow="md">
              <Title order={3} mb="md">
                {modelName}
              </Title>
              <LineChart
                h={400}
                data={data}
                dataKey="date"
                series={series}
                yAxisProps={{
                  reversed: true, // Lower position numbers should be at top
                  domain: [
                    1,
                    Math.max(
                      ...data.flatMap((d) =>
                        Object.values(d).filter((v) => typeof v === "number")
                      )
                    ) || 10,
                  ],
                }}
                xAxisProps={{
                  tick: { fontSize: 12 },
                  angle: -45,
                }}
                gridAxis="xy"
                withLegend
                legendProps={{
                  verticalAlign: "top",
                  height: 60,
                }}
              />
              <Text size="sm" c="dimmed" mt="sm">
                Lower values indicate better ranking positions. {monitoredBrandName} is highlighted in blue.
                {selectedModel === "all" && " Positions are averaged across all models."}
              </Text>
            </Paper>
          );
        })}
      </Stack>
    </Container>
  );
}
